import { create } from "zustand";
import * as XLSX from "xlsx";
import { PLATFORM_KEYS } from "@/constants/platformData";
import { extractUsername } from "../utils/urlUtils";
import type { DateRangeType } from "@/types/shared.types";

export interface InfluencerData {
  facebook: string;
  twitter: string;
  instagram: string;
  reddit: string;
  yt: string;
  tiktok: string;
}

interface InfluencerState {
  influencerData: InfluencerData[];
  inputUsernames: string[];
  setInputUsernames: (usernames: string[]) => void;
  isLoading: boolean;
  error: string | null;
  showLinks: boolean;
  progress: number;
  limit: number;
  setLimit: (limit: number) => void;

  dateRange: DateRangeType;
  setDateRange: (dateRange: DateRangeType) => void;

  setInfluencerData: (data: InfluencerData[]) => void;
  addInfluencer: (influencer: InfluencerData) => void;
  removeInfluencer: (index: number) => void;
  clearInfluencerData: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setShowLinks: (showLinks: boolean) => void;
  setProgress: (progress: number) => void;
  syncInputUsernames: () => void;

  parseExcelFile: (
    file: File,
    onProgress?: (progress: number) => void,
  ) => Promise<void>;
  uploadInfluencersFile: (
    file: File,
    onProgress?: (progress: number) => void,
  ) => Promise<void>;
}

export const useInfluencerStore = create<InfluencerState>((set, get) => ({
  influencerData: [
    {
      facebook: "",
      twitter: "",
      instagram: "",
      reddit: "",
      yt: "",
      tiktok: "",
    },
  ],
  inputUsernames: [],
  isLoading: false,
  error: null,
  showLinks: true,
  progress: 0,

  limit: 10,
  setLimit: (limit) => set({ limit }),

  dateRange: "last_24_hours",
  setDateRange: (dateRange) => set({ dateRange }),

  setInfluencerData: (data) => set({ influencerData: data }),
  setInputUsernames: (names) => set({ inputUsernames: names }),
  setShowLinks: (showLinks) => set({ showLinks }),
  setProgress: (progress) => set({ progress }),

  addInfluencer: (influencer) =>
    set((state) => ({
      influencerData: [...state.influencerData, influencer],
    })),

  removeInfluencer: (index) =>
    set((state) => ({
      influencerData: state.influencerData.filter((_, i) => i !== index),
    })),

  clearInfluencerData: () => set({ influencerData: [] }),

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error }),

  syncInputUsernames: () => {
    const { influencerData, setInputUsernames } = get();

    const usernamesWithPlatform = new Set<string>();

    influencerData.forEach((row) => {
      PLATFORM_KEYS.forEach((platform) => {
        const raw = row[platform];
        if (raw) {
          const extracted = extractUsername(raw, platform);
          if (extracted) {
            usernamesWithPlatform.add(`${extracted}:${platform}`);
          }
        }
      });
    });

    setInputUsernames(Array.from(usernamesWithPlatform));
  },

  parseExcelFile: async (
    file: File,
    onProgress?: (progress: number) => void,
  ) => {
    const { setLoading, setError, setInfluencerData, setProgress } = get();

    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      const data = await new Promise<InfluencerData[]>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            setProgress(20);
            onProgress?.(20);

            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });

            setProgress(40);
            onProgress?.(40);

            const worksheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[worksheetName];

            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
            });

            setProgress(60);
            onProgress?.(60);

            if (jsonData.length === 0) {
              reject(new Error("File is empty"));
              return;
            }

            const headers = jsonData[0] as string[];
            if (headers.length < 6) {
              reject(
                new Error(
                  `File must have at least 6 columns. Found: ${headers.length}`,
                ),
              );
              return;
            }

            const requiredHeaders = [
              "Facebook",
              "X (Twitter)",
              "Instagram",
              "Reddit",
              "YouTube",
              "TikTok",
            ];

            const missingHeaders = requiredHeaders.filter((requiredHeader) => {
              const normalizedRequired = requiredHeader
                .toLowerCase()
                .replace(" (", "")
                .replace(")", "")
                .replace("x ", "")
                .replace("twitter", "x");

              return !headers.some((header) => {
                if (!header) return false;
                const normalizedHeader = header
                  .toString()
                  .toLowerCase()
                  .replace(" (", "")
                  .replace(")", "")
                  .replace("x ", "")
                  .replace("twitter", "x");

                return (
                  normalizedHeader.includes(normalizedRequired) ||
                  normalizedRequired.includes(normalizedHeader)
                );
              });
            });

            if (missingHeaders.length > 0) {
              console.warn("Some headers don't match exactly:", missingHeaders);
            }

            if (jsonData.length <= 1) {
              reject(new Error("No data rows found in the file"));
              return;
            }

            setProgress(70);
            onProgress?.(70);

            const processedData: InfluencerData[] = [];
            const totalRows = jsonData.length - 1;

            for (let i = 1; i < jsonData.length; i++) {
              const row = jsonData[i] as string[] | undefined;
              if (!row) continue;

              processedData.push({
                facebook: row[0]?.trim() || "",
                twitter: row[1]?.trim() || "",
                instagram: row[2]?.trim() || "",
                reddit: row[3]?.trim() || "",
                yt: row[4]?.trim() || "",
                tiktok: row[5]?.trim() || "",
              });

              const rowProgress = 70 + Math.floor(((i - 1) / totalRows) * 25);
              setProgress(rowProgress);
              onProgress?.(rowProgress);
            }

            if (processedData.length === 0) {
              reject(new Error("No valid data rows found in the file"));
              return;
            }

            setProgress(100);
            onProgress?.(100);

            resolve(processedData);
          } catch {
            reject(new Error("Failed to parse Excel file"));
          }
        };

        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsArrayBuffer(file);
      });

      setInfluencerData(data);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to parse file");
    } finally {
      setLoading(false);
    }
  },

  uploadInfluencersFile: async (
    file: File,
    onProgress?: (progress: number) => void,
  ) => {
    await get().parseExcelFile(file, onProgress);
  },
}));
